import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WallClock } from 'components/views/molecules/WallClock';
import { ignore } from 'utility/ignore';
import { useSetNavbarVisibility } from 'services/useNavbarVisibility';
import styled from '@emotion/styled';
import { CSSProperties } from '@emotion/serialize';

const Frame = styled.div({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const Upper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
    lineHeight: 1,
});

const Middle = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
});

const FirstDigits = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const SecondDigits = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const ThirdDigits = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const ColorBar = styled.div<{ color: CSSProperties['backgroundColor'] }>(p => ({
    backgroundColor: p.color,
    height: '3vh',
    width: '5%',
}));

const ColorBlock = styled.div({
    height: '6vh',
    width: '100%',
    border: 'solid white 1px',
    backgroundColor: 'black',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

export type ClockPageProps = {}

export function ClockPage(props: ClockPageProps) {
    ignore(props);

    const showNavbar = useSetNavbarVisibility();
    useEffect(() => {
        showNavbar(false);
        return () => showNavbar(true);
    }, [showNavbar]);

    const resolution = 10;
    const refFirstDigits = useRef<HTMLDivElement>(null);
    const refSecondDigits = useRef<HTMLDivElement>(null);
    const refThirdDigits = useRef<HTMLDivElement>(null);
    const refColorIndex = useRef<number>(0);
    const [colors] = useState(() => ['red', 'blue', 'green', 'yellow', 'purple', 'skyblue', 'lawngreen']);
    const refTime = useRef<HTMLDivElement>(null);
    const renderer = useCallback((now: Date, html: HTMLDivElement, defaultRenderer: (now: Date, html: HTMLDivElement) => void) => {
        defaultRenderer(now, html);

        if (refTime.current) {
            const h = now.getHours().toString().padStart(2, '0');
            const m = now.getMinutes().toString().padStart(2, '0');
            const s = now.getSeconds().toString().padStart(2, '0');
            refTime.current.innerText = `${h}:${m}:${s}.`;
        }

        if (refTime.current) {
            const ms = now.getMilliseconds().toString().padStart(3, '0');
            html.innerText = `${ms}`;
        }

        if (!refFirstDigits.current || !refSecondDigits.current || !refThirdDigits.current) return;

        const color = colors[refColorIndex.current];
        refColorIndex.current += 1;
        refColorIndex.current %= colors.length;

        const msec = now.getUTCMilliseconds();

        const firstPivot = Math.floor((msec / (1000 / resolution)) % 10);
        let j = 0;
        for (const block of refFirstDigits.current.children) {
            (block as HTMLDivElement).style.visibility = (j > firstPivot) ? 'hidden' : 'visible';
            (block as HTMLDivElement).style.backgroundColor = 'black';
            ++j;
        }
        if (firstPivot >= 0) {
            (refFirstDigits.current.children[firstPivot] as HTMLDivElement).style.backgroundColor = color;
        }

        const secondPivot = Math.floor((msec / (100 / resolution)) % 10);
        let i = 0;
        for (const block of refSecondDigits.current.children) {
            (block as HTMLDivElement).style.visibility = (i > secondPivot) ? 'hidden' : 'visible';
            (block as HTMLDivElement).style.backgroundColor = 'black';
            ++i;
        }
        if (secondPivot >= 0) {
            (refSecondDigits.current.children[secondPivot] as HTMLDivElement).style.backgroundColor = color;
        }

        const thirdPivot = Math.floor((msec / (10 / resolution)) % 10);
        let k = 0;
        for (const block of refThirdDigits.current.children) {
            (block as HTMLDivElement).style.visibility = (k > thirdPivot) ? 'hidden' : 'visible';
            (block as HTMLDivElement).style.backgroundColor = 'black';
            ++k;
        }
        if (thirdPivot >= 0) {
            (refThirdDigits.current.children[thirdPivot] as HTMLDivElement).style.backgroundColor = color;
        }
    }, [colors]);
    const [blockPlaceholder] = useState(() => [...Array(resolution)]);

    return (
        <Frame>
            <Upper>
                <div ref={refTime} style={{ fontSize: '8vw' }} />
                <WallClock fontSize={'30vw'} renderer={renderer} />
            </Upper>
            <Middle>
                {colors.map((col, i) =>
                    <ColorBar key={i} color={col} />
                )}
            </Middle>
            <FirstDigits ref={refFirstDigits}>
                {blockPlaceholder.map(
                    (_, i) => <ColorBlock key={`${i}`} />
                )}
            </FirstDigits>
            <SecondDigits ref={refSecondDigits}>
                {blockPlaceholder.map(
                    (_, i) => <ColorBlock key={`${i}`} />
                )}
            </SecondDigits>
            <ThirdDigits ref={refThirdDigits}>
                {blockPlaceholder.map(
                    (_, i) => <ColorBlock key={`${i}`} />
                )}
            </ThirdDigits>
        </Frame>
    );
}
